// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import mediator from "@tvg/mediator";
import type { Dispatch } from "redux";
import { get } from "lodash";

import ModalV2 from "@tvg/atomic-ui/_templates/ModalV2";
import type { HandicapOptionsType } from "@tvg/types/Race";
import HandicappingList from "@tvg/atomic-ui/_templates/HandicappingList/index";
import { getHandicappingMode } from "@tvg/utils/handicappingUtils";
import performanceUtils from "@tvg/utils/performanceUtils";
import {
  setHandicappingFavoriteModeAction,
  setHandicappingModeAction,
  setHandicappingModalStateAction,
  userSetHandicappingModeAction,
  type Actions
} from "@tvg/shared-actions/ProgramActions";
import {
  setNewPreferenceValue,
  type SetNewPreferenceValue
} from "@tvg/shared-actions/UserActions";
import uam from "@tvg/api/uam";

type Props = {
  dispatch: Dispatch<*>,
  handicappingMode: HandicapOptionsType,
  userData: { logged: boolean },
  isGreyhound: boolean,
  isOpen: boolean,
  isVideoShown: boolean,
  handicappingInfo: boolean,
  modalType: "bottom" | "right",
  showFavorite: boolean
};

type State = {
  favoriteMode: HandicapOptionsType
};

export class HandicappingModalComponent extends Component<Props, State> {
  static defaultProps = {
    userData: { logged: false },
    handicappingMode: "basic",
    isGreyhound: false,
    isOpen: false,
    isVideoShown: false,
    handicappingInfo: false,
    modalType: "bottom",
    showFavorite: true
  };

  constructor(props: Props): void {
    super(props);

    this.state = {
      favoriteMode: "basic"
    };
  }

  componentDidMount() {
    performanceUtils.measure("page_load", { page: "handicapping" });
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    let returnState = null;

    const currentFavoriteHandicappingMode = prevState.favoriteMode;

    const nextFavoriteHandicappingMode = get(
      nextProps.userData,
      "preferences.handicappingFavoriteMode",
      "basic"
    );

    if (currentFavoriteHandicappingMode !== nextFavoriteHandicappingMode) {
      returnState = {
        favoriteMode: nextFavoriteHandicappingMode
      };
    }

    return returnState;
  }

  onFavoriteSelectOption = (mode: HandicapOptionsType, description: string) => {
    mediator.base.dispatch({
      type: "HANDICAPPING:SELECT_FAVORITE_DATA_TYPE",
      payload: {
        type: description
      }
    });

    if (!this.props.userData.logged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: this.selectionFavoriteCallBack(mode),
          triggerAction: "handicapping_select_favorite_data_type"
        }
      });
      return;
    }

    uam
      .setUserPref(
        +get(this.props.userData, "user.accountNumber", ""),
        "handicappingFavoriteMode",
        mode
      )
      .then((data) => {
        if (data.status === 200) {
          this.props.dispatch(setHandicappingFavoriteModeAction(mode));
          this.setState({ favoriteMode: mode });

          this.props.dispatch(
            setNewPreferenceValue("handicappingFavoriteMode", mode)
          );
        }
      });
  };

  onSelectOption = (mode: HandicapOptionsType, description: string) => {
    mediator.base.dispatch({
      type: "HANDICAPPING:SELECT_DATA_TYPE",
      payload: {
        type: description
      }
    });

    this.props.dispatch(setHandicappingModalStateAction(false));

    if (!this.props.userData.logged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: () => {
            this.props.dispatch(userSetHandicappingModeAction(mode));
            this.selectionCallBack(mode);
          },
          triggerAction: "handicapping_select_data_type"
        }
      });
      return;
    }
    this.props.dispatch(setHandicappingModeAction(mode));
  };

  onLogin = () => {
    mediator.base.dispatch({
      type: "OPEN_LOGIN",
      payload: {
        triggerAction: "handicapping_login"
      }
    });
    this.props.dispatch(setHandicappingModalStateAction(false));
  };

  onSignUp = () => {
    mediator.base.dispatch({
      type: "OPEN_REGISTRATION",
      payload: {
        callback: () =>
          this.props.dispatch(setHandicappingModalStateAction(true))
      }
    });
    this.props.dispatch(setHandicappingModalStateAction(false));
  };

  selectionFavoriteCallBack = (mode: HandicapOptionsType) =>
    this.props.dispatch(setHandicappingFavoriteModeAction(mode));

  selectionCallBack = (mode: HandicapOptionsType) =>
    this.props.dispatch(setHandicappingModeAction(mode));

  closeModal = () => {
    this.props.dispatch(setHandicappingModalStateAction(false));

    mediator.base.dispatch({
      type: "HANDICAPPING:CLOSE_MODAL"
    });
  };

  render() {
    const baseProps = {
      title: "Handicapping Info",
      animation: this.props.modalType,
      isOpen: this.props.isOpen,
      onClose: this.closeModal,
      qaLabel: "modal-handicapping"
    };

    const props =
      this.props.modalType === "bottom"
        ? {
            ...baseProps,
            hasVideoOffset: this.props.isVideoShown,
            isFullHeight: false
          }
        : {
            ...baseProps,
            isFullWidth: false,
            offsetTop: 0,
            hasRoundedCorners: false
          };

    return (
      <ModalV2 {...props}>
        {() => (
          <HandicappingList
            favoriteItem={this.state.favoriteMode}
            activeItem={getHandicappingMode(
              this.props.userData.logged,
              this.props.handicappingMode,
              get(this.props, "isGreyhound")
            )}
            isLogged={this.props.userData.logged}
            isGreyhound={this.props.isGreyhound}
            loginCallBack={this.onLogin}
            signupCallBack={this.onSignUp}
            onFavoriteHandicapSelect={this.onFavoriteSelectOption}
            onHandicapSelect={this.onSelectOption}
            showFavorite={
              this.props.handicappingInfo && this.props.showFavorite
            }
          />
        )}
      </ModalV2>
    );
  }
}

export default connect(
  (
    store,
    props: {
      isOpen: boolean,
      isGreyhound: boolean
    }
  ) => ({
    isGreyhound: props.isGreyhound,
    userData: store.userData,
    handicappingMode: store.RaceProgram.handicappingMode,
    isVideoShown: store.RaceProgramVideo.show,
    handicappingInfo: get(store, "capi.featureToggles.handicappingInfo", false)
  }),
  (dispatch: Dispatch<Actions | SetNewPreferenceValue>) => ({ dispatch })
)(HandicappingModalComponent);
